import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Tabs variants. `default` is the segmented control: a muted track with the
 * active tab raised on it. `underline` is page navigation (the campaign
 * header): plain tabs, the active one in primary with a bar under it. The
 * variant is set once on <TabsList>; its triggers read it from context.
 *
 * The underline bar hangs in the list's bottom padding (`py-2` on the list,
 * `-mb-2` on the bar), so it lands on the bottom edge of the list — put the
 * list flush against a header's bottom border and the bar sits on it.
 */
const tabsListVariants = cva("inline-flex w-fit items-center justify-center", {
  variants: {
    variant: {
      default: "bg-muted text-muted-foreground/70 rounded-md p-0.5",
      underline: "text-foreground gap-2 py-2",
    },
  },
  defaultVariants: { variant: "default" },
});

const tabsTriggerVariants = cva(
  "hover:text-foreground cursor-pointer focus-visible:border-ring focus-visible:ring-ring/20 inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0 [&_svg]:opacity-60 data-[state=active]:[&_svg]:opacity-100 hover:[&_svg]:opacity-100",
  {
    variants: {
      variant: {
        default:
          "transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs",
        underline:
          "relative hover:bg-muted-foreground/10 data-[state=active]:text-primary after:absolute after:inset-x-0 after:bottom-0 after:-mb-2 after:h-0.75 after:rounded-t-pill data-[state=active]:after:bg-primary",
      },
    },
    defaultVariants: { variant: "default" },
  }
);

type TabsVariant = NonNullable<
  VariantProps<typeof tabsListVariants>["variant"]
>;

const TabsVariantContext = React.createContext<TabsVariant>("default");

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  );
}

function TabsList({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  const resolved = variant ?? "default";
  return (
    <TabsVariantContext.Provider value={resolved}>
      <TabsPrimitive.List
        data-slot="tabs-list"
        data-variant={resolved}
        className={cn(tabsListVariants({ variant: resolved, className }))}
        {...props}
      />
    </TabsVariantContext.Provider>
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const variant = React.useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant, className }))}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsContent, TabsList, TabsTrigger, tabsListVariants, tabsTriggerVariants };
